import {
  Box,
  SimpleGrid,
  Text,
  Flex,
  Icon,
  Button,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiUsers,
  FiUserCheck,
  FiMail,
  FiSmartphone,
  FiPlay,
  FiCheck,
  FiX,
  FiClock,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import Sidebar from "../Sidebar";
import ReportsSection from "./ReportsSection";
import LoginAnalytics from "./LoginAnalytics";
import { FaRupeeSign } from "react-icons/fa";
import { useEffect, useState } from "react";
import axios from "axios";
import { api_url } from "../../url";
import { IoGameControllerOutline } from "react-icons/io5";
import { FiDatabase } from "react-icons/fi";


const StatCard = ({ icon, label, value, color, to }) => {
  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue(`${color}.200`, `${color}.700`);
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const labelColor = useColorModeValue("gray.600", "gray.400");
  const navigate = useNavigate();

  return (
    <Flex
      p={6}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="lg"
      align="center"
      justify="space-between"
      bg={bg}
      boxShadow="sm"
      cursor={to ? "pointer" : "default"}
      _hover={to ? { boxShadow: "md", transform: "translateY(-2px)" } : {}}
      transition="0.2s"
      onClick={() => to && navigate(to)}
    >
      <Flex align="center" gap={4}>
        <Box p={3} bg={iconBg} borderRadius="md">
          <Icon as={icon} boxSize={6} color={color} />
        </Box>
        <Box>
          <Text fontSize="sm" color={labelColor}>
            {label}
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {value}
          </Text>
        </Box>
      </Flex>
      <Text fontSize="2xl" color={labelColor}>
        ›
      </Text>
    </Flex>
  );
};

const InfoBox = ({ icon, label, value, color, to }) => {
  const iconBg = useColorModeValue(`${color}.50`, `${color}.900`);
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const navigate = useNavigate();
  const bg = useColorModeValue("white", "gray.800");

  return (
    <Flex
      align="center"
      gap={4}
      bg={bg}
      p={3}
      borderRadius="md"
      boxShadow="sm"
      cursor={to ? "pointer" : "default"}
      _hover={to ? { boxShadow: "md", transform: "translateY(-2px)" } : {}}
      transition="0.2s"
      onClick={() => to && navigate(to)}
    >
      <Box p={3} bg={iconBg} borderRadius="md">
        <Icon as={icon} boxSize={5} color={color} />
      </Box>
      <Box>
        <Text fontSize="sm" color={labelColor}>
          {label}
        </Text>
        <Text fontWeight="bold">{value}</Text>
      </Box>
    </Flex>
  );
};

const SIDEBAR_WIDTH = "250px";

export default function Home() {
  const bg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");

  const [stats, setStats] = useState({
    users: { total: 0, active: 0, emailUnverified: 0, mobileUnverified: 0 },
    deposits: { totalAmount: 0, successAmount: 0, pendingCount: 0, rejectedCount: 0 },
    withdrawals: { totalAmount: 0, successAmount: 0, pendingCount: 0, rejectedCount: 0 },
    casino: { totalProviders: 0, totalGames: 0, activeGames: 0, inactiveGames: 0 }
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${api_url}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard stats", error);
    }
  };

  return (
    <>
      <Navbar />
      <Sidebar />
      <Box
        bg={bg}
        mt="80px"
        minH="100vh"
        p={6}
        ml={{ base: 0, md: SIDEBAR_WIDTH }}
      >
        <Flex justify="space-between" mb={6}>
          <Text fontSize="2xl" fontWeight="bold">
            Dashboard Overview
          </Text>
          <Button 
            bg="#4f46e5" 
            color="white"
            size="sm" 
            leftIcon={<FiClock />}
            _hover={{ bg: "#3a2bd1" }}
            onClick={() => navigate("/cron-jobs")}
          >
            Cron Jobs
          </Button>
        </Flex>

        {/* User Stats */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <StatCard
            icon={FiUsers}
            label="Total Players"
            value={stats.users.total}
            color="blue"
            to="/users"
          />
          <StatCard
            icon={FiUserCheck}
            label="Active (24h)"
            value={stats.users.active}
            color="green"
            to="/users"
          />
          <StatCard
            icon={FiMail}
            label="Email Unverified"
            value={stats.users.emailUnverified}
            color="red"
            to="/users"
          />
          <StatCard
            icon={FiSmartphone}
            label="Mobile Unverified"
            value={stats.users.mobileUnverified}
            color="orange"
            to="/users"
          />
        </SimpleGrid>

        {/* Casino Stats */}
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6} mt={6}>
          <StatCard
            icon={FiDatabase}
            label="Total Providers"
            value={stats.casino.totalProviders}
            color="purple"
            to="/casino/providers"
          />
          <StatCard
            icon={IoGameControllerOutline}
            label="Total Casino Games"
            value={stats.casino.totalGames}
            color="teal"
            to="/casino/games"
          />
          <StatCard
            icon={FiCheck}
            label="Active Games"
            value={stats.casino.activeGames}
            color="green"
            to="/casino/games"
          />
          <StatCard
            icon={FiX}
            label="Inactive Games"
            value={stats.casino.inactiveGames}
            color="red"
            to="/casino/games"
          />
        </SimpleGrid>

        {/* Financial Cards */}
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mt={8}>
          {/* Deposits */}
          <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="sm">
            <Text fontSize="xl" fontWeight="bold" mb={4} textAlign={"left"}>
              Deposits Summary
            </Text>
            <SimpleGrid columns={2} spacing={4}>
              <InfoBox
                icon={FaRupeeSign}
                label="Total Successful"
                value={`₹${stats.deposits.successAmount.toLocaleString()}`}
                color="green"
                to="/deposit-successful"
              />
              <InfoBox
                icon={FiPlay}
                label="Pending Deposits"
                value={stats.deposits.pendingCount}
                color="orange"
                to="/deposit-pending"
              />
              <InfoBox
                icon={FiX}
                label="Rejected Deposits"
                value={stats.deposits.rejectedCount}
                color="red"
                to="/deposit-rejected"
              />
              <InfoBox
                icon={FaRupeeSign}
                label="Total Volume"
                value={`₹${stats.deposits.totalAmount.toLocaleString()}`}
                color="blue"
                to="/deposit-all"
              />
            </SimpleGrid>
          </Box>

          {/* Withdrawals */}
          <Box bg={cardBg} p={6} borderRadius="lg" boxShadow="sm">
            <Text fontSize="xl" fontWeight="bold" mb={4} textAlign={"left"}>
              Withdrawals Summary
            </Text>
            <SimpleGrid columns={2} spacing={4}>
              <InfoBox
                icon={FaRupeeSign}
                label="Total Paid"
                value={`₹${stats.withdrawals.successAmount.toLocaleString()}`}
                color="green"
                to="/withdrawal-approved"
              />
              <InfoBox
                icon={FiPlay}
                label="Pending Requests"
                value={stats.withdrawals.pendingCount}
                color="orange"
                to="/withdrawal-pending"
              />
              <InfoBox
                icon={FiX}
                label="Rejected Requests"
                value={stats.withdrawals.rejectedCount}
                color="red"
                to="/withdrawal-rejected"
              />
              <InfoBox
                icon={FaRupeeSign}
                label="Total Requested"
                value={`₹${stats.withdrawals.totalAmount.toLocaleString()}`}
                color="blue"
                to="/withdrawal-all"
              />
            </SimpleGrid>
          </Box>
        </SimpleGrid>

        <ReportsSection />
        <LoginAnalytics />
      </Box>
    </>
  );
}
